import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, PhoneCall, Languages } from "lucide-react";
import { PHONE, PHONE_HREF } from "@/data";

const NAV = [
  { to: "/services", label: "Services", testid: "nav-services-link" },
  { to: "/locations", label: "Wales Locations", testid: "nav-locations-link" },
  { to: "/insights", label: "Insights", testid: "nav-insights-link" },
  { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isCy = pathname.startsWith("/cy");
  const langLink = isCy ? { to: "/", label: "EN" } : { to: "/cy", label: "CYM" };

  return (
    <header className="fixed top-0 left-0 right-0 z-40 border-b border-[#1C2027] bg-[#0A0C10]/85 backdrop-blur-xl" data-testid="site-header">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" className="group flex items-center gap-3" data-testid="nav-logo" onClick={() => setOpen(false)}>
          <span className="flex h-9 w-9 items-center justify-center bg-[#F5A800] transition-transform duration-300 group-hover:rotate-90">
            <svg viewBox="0 0 64 64" className="h-5 w-5" aria-hidden="true">
              <path d="M18 8 L32 26 L46 8 L58 8 L39 32 L58 56 L46 56 L32 38 L18 56 L6 56 L25 32 L6 8 Z" fill="#0A0C10" />
            </svg>
          </span>
          <span className="leading-none">
            <span className="font-condensed block text-lg font-bold uppercase tracking-wide text-white">Xtreme Protection</span>
            <span className="block text-[10px] font-semibold uppercase tracking-[0.28em] text-[#F5A800]">Wales Security Services</span>
          </span>
        </Link>
        <nav className="hidden items-center gap-8 md:flex">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={n.testid} className="text-[13px] font-semibold uppercase tracking-[0.18em] text-white/70 transition-colors duration-300 hover:text-[#F5A800]">
              {n.label}
            </Link>
          ))}
          <Link
            to={langLink.to}
            data-testid="nav-language-toggle"
            className="flex items-center gap-1.5 border border-[#1C2027] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-white/70 transition-colors duration-300 hover:border-[#F5A800] hover:text-[#F5A800]"
          >
            <Languages className="h-3.5 w-3.5" />
            {langLink.label}
          </Link>
          <span className="hidden items-center gap-2 border border-[#1C2027] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60 lg:flex">
            <span className="animate-pulse-dot h-1.5 w-1.5 rounded-full bg-emerald-400" />
            24/7 Wales Command
          </span>
          <a href={PHONE_HREF} data-testid="nav-phone-link" className="flex items-center gap-2 bg-[#F5A800] px-4 py-2 text-[13px] font-bold uppercase tracking-[0.14em] text-[#0A0C10] transition-colors duration-300 hover:bg-white">
            <PhoneCall className="h-4 w-4" />
            {PHONE}
          </a>
        </nav>
        <button className="text-white md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="Toggle menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-[#1C2027] bg-[#0A0C10] md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-4 py-4">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} data-testid={`mobile-${n.testid}`} onClick={() => setOpen(false)} className="px-2 py-3 text-sm font-semibold uppercase tracking-[0.18em] text-white/80 hover:text-[#F5A800]">
                  {n.label}
                </Link>
              ))}
              <Link to={langLink.to} data-testid="mobile-nav-language-toggle" onClick={() => setOpen(false)} className="flex items-center gap-2 px-2 py-3 text-sm font-semibold uppercase tracking-[0.18em] text-white/80 hover:text-[#F5A800]">
                <Languages className="h-4 w-4" />
                {langLink.label === "CYM" ? "Cymraeg" : "English"}
              </Link>
              <a href={PHONE_HREF} data-testid="mobile-nav-phone-link" className="mt-2 flex items-center justify-center gap-2 bg-[#F5A800] px-4 py-3 text-sm font-bold uppercase tracking-[0.14em] text-[#0A0C10]">
                <PhoneCall className="h-4 w-4" />
                {PHONE}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
